import { LockOutlined as LockIcon } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Box, Button, Chip, Card, Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { NotificationSettings as NotificationSettingsType } from '../../../types/user';
import ResponsiveTooltip from '../../ResponsiveTooltip';

interface ProfileSectionProps {
    email: string;
    isGoogleUser: boolean;
    notificationSettings: NotificationSettingsType;
    isLoading: boolean;
    supportId?: string;
    emailVerified?: boolean;
    onChangeEmail: () => void;
    onChangePassword: () => void;
    onUnlinkGoogle: () => void;
    onSettingChange: (key: keyof NotificationSettingsType) => void;
    onResendVerification?: () => void;
    currentCtUser?: any;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({
    email,
    isGoogleUser,
    notificationSettings,
    isLoading,
    supportId,
    emailVerified,
    onChangeEmail,
    onChangePassword,
    onUnlinkGoogle,
    onSettingChange,
    onResendVerification,
    currentCtUser,
}) => {
    const { t } = useTranslation();

    return (
        <Card sx={{ p: 3 }}>
            <Typography
                variant="h6"
                sx={{ mb: 3 }}
            >
                {t('settings.profile.title')}
            </Typography>

            <Stack spacing={3}>
                {/* Email section */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '160px 1fr', 
                    gap: 1,
                    alignItems: 'start' 
                }}>
                    {/* Column 1: Label */}
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        fontWeight="bold"
                    >
                        {t('settings.account.email')}:
                    </Typography>
                    
                    {/* Column 2: Value + Badge + Buttons */}
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ color: 'text.primary' }}>
                                {email}
                            </Typography>
                            {/* Email verification badge inline */}
                            {!isGoogleUser && !emailVerified && (
                                <Chip
                                    label={t('settings.account.emailNotVerified')}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{
                                        fontSize: '0.75rem',
                                        height: '28px',
                                    }}
                                />
                            )}
                        </Box>

                        {!isGoogleUser && (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<EmailIcon />}
                                    onClick={onChangeEmail}
                                    sx={{
                                        height: '32px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        alignSelf: 'flex-start',
                                    }}
                                >
                                    {t('settings.account.buttons.changeEmail')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<LockIcon />}
                                    onClick={onChangePassword}
                                    sx={{
                                        height: '32px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        alignSelf: 'flex-start',
                                    }}
                                >
                                    {t('settings.account.buttons.changePassword')}
                                </Button>
                                {!emailVerified && onResendVerification && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<MarkEmailReadIcon />}
                                        onClick={onResendVerification}
                                        sx={{
                                            color: 'warning.main',
                                            borderColor: 'warning.main',
                                            height: '32px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            alignSelf: 'flex-start',
                                            '&:hover': {
                                                borderColor: 'warning.dark',
                                                backgroundColor: 'warning.lighter',
                                            },
                                        }}
                                    >
                                        {t('settings.account.buttons.resendVerification')}
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Box>

                {/* User ID section with info icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        fontWeight="bold"
                        sx={{ minWidth: '160px' }}
                    >
                        User-ID:
                    </Typography>
                    <Typography variant="body1">
                        {supportId || '-'}
                    </Typography>
                    <ResponsiveTooltip
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.support.description')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    },
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    },
                                },
                            },
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }}
                        />
                    </ResponsiveTooltip>
                </Box>

                {/* Country */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.country')}:
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.tax.values.germany')}
                    </Typography>
                </Box>

                {/* Currency */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.localCurrency')}:
                    </Typography>
                    <Typography variant="body1">
                        {currentCtUser?.currency || 'EUR'}
                    </Typography>
                </Box>

                {/* Inventory Method */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.inventoryMethod')}:
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.tax.values.fifo')}
                    </Typography>
                    <ResponsiveTooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.tax.tooltips.inventoryMethod')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    },
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    },
                                },
                            },
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }} 
                        />
                    </ResponsiveTooltip>
                </Box>

                {/* Cost Basis */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" color="text.secondary" fontWeight="bold" sx={{ minWidth: '160px' }}>
                        {t('settings.tax.costBasis')}:
                    </Typography>
                    <Typography variant="body1">
                        {t('settings.tax.values.perAccount')}
                    </Typography>
                    <ResponsiveTooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.tax.tooltips.costBasis')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    },
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    },
                                },
                            },
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }} 
                        />
                    </ResponsiveTooltip>
                </Box>

                {/* Notifications section */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            fontWeight="bold"
                            sx={{ minWidth: '160px' }}
                        >
                            {t('settings.notifications.title', 'Benachrichtigungen')}:
                        </Typography>
                        <Typography variant="body1">
                            {t('settings.notifications.productUpdates')}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ ml: '160px' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => onSettingChange('productUpdates')}
                            disabled={isLoading}
                            startIcon={notificationSettings.productUpdates ? <MarkEmailReadIcon /> : <EmailOutlinedIcon />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                color: notificationSettings.productUpdates ? 'primary.main' : 'text.primary',
                                borderColor: notificationSettings.productUpdates ? 'primary.main' : 'divider',
                                bgcolor: 'transparent',
                                height: '32px',
                                alignSelf: 'flex-start',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                    borderColor: notificationSettings.productUpdates ? 'primary.main' : 'divider',
                                },
                            }}
                        >
                            {notificationSettings.productUpdates
                                ? t('settings.notifications.unsubscribe')
                                : t('settings.notifications.subscribe')}
                        </Button>
                    </Box>
                </Box>
            </Stack>
        </Card>
    );
};

export default ProfileSection;
